import React, { useState, useMemo } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiCheck,
  FiEye,
} from "react-icons/fi";
import { FaBrain } from "react-icons/fa";
import {
  formatColumnHeader,
  getAdditionalFields,
  getHintsFromProtocol,
  getV2TableColumnKeys,
  isContrastYes,
  isV3Model,
  pick,
  protocolDisplayTitle,
  protocolRowsEqual,
} from "./protocolPanelUtils";

function ProtocolField({ label, value, wide }) {
  const display = value != null && String(value).trim() !== "" ? String(value) : "—";
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5">
        {label}
      </p>
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{display}</p>
    </div>
  );
}

export function ProtocolAnatomyRow({
  detectedAnatomy,
  unknownAnatomy,
  onCopy,
  t,
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t("detectedAnatomy")}
        </span>
        <span
          className={
            unknownAnatomy
              ? "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-sm font-medium"
              : "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E8F5C8] dark:bg-green-900/40 text-green-900 dark:text-green-200 text-sm font-medium"
          }
        >
          {!unknownAnatomy && <FaBrain className="shrink-0" aria-hidden />}
          {detectedAnatomy || "—"}
        </span>
        {(detectedAnatomy || "").length > 0 && (
          <button
            type="button"
            onClick={() => onCopy(detectedAnatomy, "anatomyLabel")}
            className="p-1 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
            title={t("copyAnatomy")}
          >
            <FiCopy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {unknownAnatomy && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t("anatomyUnknownHint")}
        </p>
      )}
    </div>
  );
}

export function ProtocolMatchStatus({ protocolMatched, t }) {
  return (
    <div className="flex flex-wrap gap-2">
      <span
        className={`px-4 py-2 rounded-lg border text-sm font-medium ${
          !protocolMatched
            ? "border-gray-800 dark:border-gray-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400"
        }`}
      >
        {t("unableToDetermine")}
      </span>
      <span
        className={`px-4 py-2 rounded-lg border text-sm font-medium ${
          protocolMatched
            ? "border-gray-800 dark:border-gray-300 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            : "border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400"
        }`}
      >
        {t("protocolMatched")}
      </span>
    </div>
  );
}

export function ProtocolUnableToDetermineAlert({ t }) {
  return (
    <div className="flex gap-3 p-4 rounded-lg border border-amber-700/40 bg-[#F5F0E8] dark:bg-amber-950/30 dark:border-amber-800">
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-amber-800 dark:border-amber-500 text-amber-900 dark:text-amber-200 text-sm font-bold"
        aria-hidden
      >
        ?
      </span>
      <div>
        <p className="font-semibold text-amber-950 dark:text-amber-100">
          {t("unableToDetermineTitle")}
        </p>
        <p className="text-sm text-amber-900/90 dark:text-amber-200/90 mt-0.5">
          {t("unableToDetermineHint")}
        </p>
      </div>
    </div>
  );
}

export function RelevantProtocolsSection({
  protocols,
  selectedProtocol,
  onSelectRow,
  onCopyTable,
  t,
}) {
  const [expanded, setExpanded] = useState(false);
  const columnKeys = useMemo(() => getV2TableColumnKeys(protocols), [protocols]);
  const count = protocols?.length ?? 0;

  if (!count) return null;

  return (
    <div className="rounded-xl border border-stone-300/80 dark:border-stone-600 bg-[#FAF8F5] dark:bg-stone-900/40 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex flex-1 items-center gap-2 flex-wrap min-w-0 text-left hover:opacity-80"
          aria-expanded={expanded}
        >
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t("relevantProtocolsForStudy")}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-200/90 dark:bg-stone-700 text-gray-700 dark:text-gray-300">
            {t("protocolsCount", { count })}
          </span>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onCopyTable}
            className="p-1.5 rounded text-gray-500 hover:bg-stone-200 dark:hover:bg-stone-700"
            title={t("copyTable")}
          >
            <FiCopy className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="p-1 rounded text-gray-600 dark:text-gray-400 hover:bg-stone-200 dark:hover:bg-stone-700"
            aria-label={expanded ? t("collapseSection") : t("expandSection")}
          >
            {expanded ? (
              <FiChevronUp className="w-5 h-5" />
            ) : (
              <FiChevronDown className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-stone-300/80 dark:border-stone-600 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-100/90 dark:bg-stone-800/80">
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                  {t("colProtocol")}
                </th>
                {columnKeys.map((key) => (
                  <th
                    key={key}
                    className="text-left px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400"
                  >
                    {formatColumnHeader(key, t)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {protocols.map((row, i) => {
                const isSelected = protocolRowsEqual(row, selectedProtocol);
                const af = getAdditionalFields(row);
                return (
                  <tr
                    key={`${pick(af, "protocol_number")}-${i}`}
                    onClick={() => onSelectRow(row)}
                    className={`border-t border-stone-200/90 dark:border-stone-700 cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-[#E8F0D8]/70 dark:bg-green-900/25"
                        : "hover:bg-stone-50 dark:hover:bg-stone-800/40"
                    }`}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      {pick(row, "protocolName") || "—"}
                    </td>
                    {columnKeys.map((key) => {
                      const val = af[key];
                      if (key === "contrast" || key === "Contrast") {
                        return (
                          <td key={key} className="px-4 py-3">
                            {isContrastYes(val) ? (
                              <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium">
                                <FiCheck className="w-4 h-4" aria-hidden />
                                {t("contrastYes")}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        );
                      }
                      if (key === "Tesla" || key === "tesla") {
                        const teslaVal = val != null ? String(val) : "";
                        return (
                          <td key={key} className="px-4 py-3">
                            {teslaVal ? (
                              <span className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-full border border-stone-400 dark:border-stone-500 px-2 text-xs font-semibold text-gray-800 dark:text-gray-200">
                                {teslaVal}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        );
                      }
                      const numKey = key === "protocol_number";
                      return (
                        <td
                          key={key}
                          className={`px-4 py-3 ${
                            numKey ? "font-bold text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {val != null && String(val).trim() !== ""
                            ? String(val)
                            : "—"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SelectedProtocolCardV2({
  protocol,
  explanation,
  language,
  onCopy,
  t,
}) {
  if (!protocol) return null;
  const af = getAdditionalFields(protocol);
  const isRtl = language === "he" || language === "ar";

  return (
    <div className="rounded-xl border-2 border-blue-400/70 dark:border-blue-500 bg-blue-50/40 dark:bg-blue-950/20 overflow-hidden">
      <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-blue-200/80 dark:border-blue-800 bg-blue-100/50 dark:bg-blue-900/30">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-800 dark:text-blue-200">
            {t("selectedProtocolHeader")}
          </p>
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-0.5">
            {protocolDisplayTitle(protocol)}
          </h3>
        </div>
        <button
          type="button"
          onClick={() =>
            onCopy(protocolDisplayTitle(protocol), "selectedProtocolLabel")
          }
          className="p-1.5 rounded hover:bg-blue-200/60 dark:hover:bg-blue-800/40"
          title={t("copySelectedProtocol")}
        >
          <FiCopy className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <ProtocolField
          label={t("colNumber")}
          value={pick(af, "protocol_number", "Protocol_Number")}
        />
        <ProtocolField label={t("colAnatomy")} value={pick(protocol, "anathomy", "anatomy")} />
        <ProtocolField
          label={t("colProtocol")}
          value={pick(protocol, "protocolName")}
          wide
        />
        <ProtocolField label={t("colModality")} value={pick(protocol, "domain", "modality")} />
        <ProtocolField
          label={t("colContrast")}
          value={pick(af, "contrast", "Contrast")}
        />
        <ProtocolField label={t("colTesla")} value={pick(af, "Tesla", "tesla")} />
      </div>
      {explanation && (
        <div className="px-4 pb-4 border-t border-blue-200/60 dark:border-blue-800">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mt-3 mb-1">
            {t("explanation")}
          </p>
          <p
            dir={isRtl ? "rtl" : "ltr"}
            className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
          >
            {explanation}
          </p>
        </div>
      )}
    </div>
  );
}

export function SelectedProtocolCardV3({
  protocol,
  explanation,
  extractedText,
  getDisplayExtractedText,
  language,
  onCopy,
  t,
}) {
  const [showExtracted, setShowExtracted] = useState(false);
  if (!protocol) return null;

  const af = getAdditionalFields(protocol);
  const hints = getHintsFromProtocol(protocol);
  const isRtl = language === "he" || language === "ar";

  return (
    <div className="rounded-xl border border-[#B8D4A0] dark:border-green-700 overflow-hidden bg-[#FAFCF7] dark:bg-gray-900/50">
      <div className="flex items-start justify-between gap-2 px-4 py-3 bg-[#E8F5C8] dark:bg-green-900/35 border-b border-[#B8D4A0] dark:border-green-800">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-green-900 dark:text-green-200 flex items-center gap-1">
            <FiCheck className="w-3.5 h-3.5" aria-hidden />
            {t("selectedProtocolHeader")}
          </p>
          <h3 className="text-lg font-bold text-green-900 dark:text-green-100 mt-0.5">
            {protocolDisplayTitle(protocol)}
          </h3>
        </div>
        <button
          type="button"
          onClick={() =>
            onCopy(protocolDisplayTitle(protocol), "selectedProtocolLabel")
          }
          className="p-1.5 rounded shrink-0 hover:bg-green-200/60 dark:hover:bg-green-800/40"
          title={t("copySelectedProtocol")}
        >
          <FiCopy className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 grid grid-cols-2 gap-px bg-stone-200/80 dark:bg-stone-700 rounded-lg overflow-hidden mx-4 mt-4 border border-stone-200 dark:border-stone-600">
        <div className="bg-white dark:bg-gray-800 p-3">
          <ProtocolField
            label={t("colNumber")}
            value={pick(af, "protocol_number", "Protocol_Number")}
          />
        </div>
        <div className="bg-white dark:bg-gray-800 p-3">
          <ProtocolField
            label={t("colAnatomy")}
            value={pick(protocol, "anathomy", "anatomy")}
          />
        </div>
        <div className="bg-white dark:bg-gray-800 p-3">
          <ProtocolField
            label={t("colProtocolLabel")}
            value={pick(protocol, "protocolName")}
          />
        </div>
        <div className="bg-white dark:bg-gray-800 p-3">
          <ProtocolField
            label={t("colModality")}
            value={pick(af, "modality", "Modality") || pick(protocol, "domain")}
          />
        </div>
      </div>

      {hints.length > 0 && (
        <div className="px-4 pt-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            {t("hintsKeywords")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {hints.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-md bg-stone-200/90 dark:bg-stone-700 text-xs text-gray-800 dark:text-gray-200"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {explanation && (
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {t("explanation")}
            </p>
            <button
              type="button"
              onClick={() => onCopy(explanation, "explanationLabel")}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              title={t("copyExplanation")}
            >
              <FiCopy className="w-3.5 h-3.5" />
            </button>
          </div>
          <p
            dir={isRtl ? "rtl" : "ltr"}
            className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
          >
            {explanation}
          </p>
        </div>
      )}

      {extractedText && (
        <div className="px-4 pb-4">
          <button
            type="button"
            onClick={() => setShowExtracted((s) => !s)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:underline"
          >
            <FiEye className="w-4 h-4" />
            {showExtracted ? t("hideExtractedText") : t("showExtractedText")}
          </button>
          {showExtracted && (
            <textarea
              readOnly
              value={getDisplayExtractedText(extractedText)}
              className="mt-2 w-full min-h-24 h-40 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-2 text-xs font-mono"
            />
          )}
        </div>
      )}
    </div>
  );
}

export function SelectedProtocolSection({
  model,
  protocol,
  explanation,
  extractedText,
  getDisplayExtractedText,
  language,
  onCopy,
  t,
}) {
  if (!protocol) return null;
  if (isV3Model(model)) {
    return (
      <SelectedProtocolCardV3
        protocol={protocol}
        explanation={explanation}
        extractedText={extractedText}
        getDisplayExtractedText={getDisplayExtractedText}
        language={language}
        onCopy={onCopy}
        t={t}
      />
    );
  }
  return (
    <SelectedProtocolCardV2
      protocol={protocol}
      explanation={explanation}
      language={language}
      onCopy={onCopy}
      t={t}
    />
  );
}
